import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, HostListener, inject, Input, OnInit, Output } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ToastrService } from 'ngx-toastr';
import { ChatConversationDetail, ChatPerson } from '../../models/chat.models';
import { ChatBackendService } from '../../services/chat-backend.service';
import { RemmiConfirmService } from '@common/remmi-confirm/remmi-confirm.service';
import { ChatStore } from '../../services/chat.store';
import { dayLabel } from '../../utils/chat-date.util';
import { ChatAvatarComponent } from '../chat-avatar/chat-avatar.component';

export type ChatGroupDialogMode = 'create' | 'manage';

/**
 * Create New Group / Manage Group.
 *
 * One component for both because they are the same form with a different starting point — a
 * name, a member picker and a selected list. In `manage` mode the tabs (Details / Members /
 * Settings) appear and edits apply immediately rather than on a final Create.
 *
 * Only the owner ever reaches `manage`; the caller hides the entry point otherwise, and the
 * server rejects it independently.
 */
@Component({
  selector: 'app-chat-group-dialog',
  standalone: true,
  imports: [CommonModule, FormsModule, ChatAvatarComponent],
  templateUrl: './chat-group-dialog.component.html',
  styleUrls: ['./chat-group-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ChatGroupDialogComponent implements OnInit {
  private readonly backend = inject(ChatBackendService);
  private readonly toastr = inject(ToastrService);
  private readonly cdr = inject(ChangeDetectorRef);
  readonly store = inject(ChatStore);
  private readonly remmiConfirm = inject(RemmiConfirmService);

  @Input() mode: ChatGroupDialogMode = 'create';

  /** The group being edited (manage mode). */
  @Input() group: ChatConversationDetail | null = null;

  /** Open straight on the Members tab — used by "Add Members" in the info panel. */
  @Input() startTab: 'details' | 'members' | 'settings' = 'details';

  @Output() closed = new EventEmitter<void>();

  readonly dayLabel = dayLabel;

  tab: 'details' | 'members' | 'settings' = 'details';
  name = '';
  search = '';
  people: ChatPerson[] = [];
  selectedIds = new Set<string>();
  loading = true;

  ngOnInit(): void {
    this.tab = this.startTab;
    this.name = this.group?.name ?? '';

    this.backend.getUsers().subscribe({
      next: people => {
        this.people = people.filter(person => person.user_id !== this.store.myId && !this.store.isHiddenPerson(person));
        this.loading = false;
        this.cdr.markForCheck();
      },
      error: () => {
        this.loading = false;
        this.cdr.markForCheck();
      },
    });
  }

  get title(): string {
    return this.mode === 'create' ? 'Create New Group' : 'Manage Group';
  }

  get filteredPeople(): ChatPerson[] {
    const term = this.search.trim().toLowerCase();
    return term ? this.people.filter(person => person.name.toLowerCase().includes(term)) : this.people;
  }

  /** In manage mode the picker only offers people who aren't already in the group. */
  get selectablePeople(): ChatPerson[] {
    if (this.mode === 'create') {
      return this.filteredPeople;
    }
    const existing = new Set((this.group?.members ?? []).map(member => member.user_id));
    return this.filteredPeople.filter(person => !existing.has(person.user_id));
  }

  get selected(): ChatPerson[] {
    return this.people.filter(person => this.selectedIds.has(person.user_id));
  }

  get canCreate(): boolean {
    return this.name.trim().length > 0 && this.selectedIds.size > 0;
  }

  isSelected(person: ChatPerson): boolean {
    return this.selectedIds.has(person.user_id);
  }

  toggle(person: ChatPerson): void {
    if (this.selectedIds.has(person.user_id)) {
      this.selectedIds.delete(person.user_id);
    } else {
      this.selectedIds.add(person.user_id);
    }
  }

  // ---- Create ----------------------------------------------------------------

  create(): void {
    if (!this.canCreate) {
      return;
    }
    this.store.createGroup(this.name.trim(), Array.from(this.selectedIds));
    this.closed.emit();
  }

  // ---- Manage ----------------------------------------------------------------

  saveName(): void {
    const next = this.name.trim();
    if (!this.group || !next || next === this.group.name) {
      return;
    }
    this.store.renameGroup(this.group.conversation_id, next);
    this.toastr.success('Group renamed.');
  }

  addSelected(): void {
    if (!this.group || !this.selectedIds.size) {
      return;
    }
    this.store.addMembers(this.group.conversation_id, Array.from(this.selectedIds));
    this.selectedIds.clear();
  }

  removeMember(userId: string, name: string): void {
    if (!this.group) {
      return;
    }
    const group = this.group;
    this.remmiConfirm
      .delete({
        title: 'Remove Member?',
        message: `They will be removed from "${group.name}".`,
        subject: name,
        confirmLabel: 'Remove',
      })
      .subscribe(isConfirmed => {
        if (isConfirmed) {
          this.store.removeMember(group.conversation_id, userId);
        }
      });
  }

  copyGroup(): void {
    if (!this.group) {
      return;
    }
    this.store.copyGroup(this.group.conversation_id);
    this.closed.emit();
  }

  /** Same wording as the Group Info panel — one action, one sentence, wherever it is offered. */
  leaveGroup(): void {
    if (!this.group) {
      return;
    }
    const group = this.group;
    this.remmiConfirm
      .ask({
        title: 'Leave Group?',
        message: "You'll stop receiving its messages.",
        subject: group.name,
        confirmLabel: 'Leave',
        tone: 'warning',
        icon: 'pi pi-sign-out',
      })
      .subscribe(isConfirmed => {
        if (isConfirmed) {
          this.store.leaveGroup(group.conversation_id);
          this.closed.emit();
        }
      });
  }

  deleteGroup(): void {
    if (!this.group) {
      return;
    }
    const group = this.group;
    this.remmiConfirm
      .delete({
        title: 'Delete Group?',
        message: 'This removes the group and its messages for everyone.',
        subject: group.name,
      })
      .subscribe(isConfirmed => {
        if (isConfirmed) {
          this.store.deleteGroup(group.conversation_id);
          this.closed.emit();
        }
      });
  }

  isMe(userId: string): boolean {
    return userId === this.store.myId;
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    this.closed.emit();
  }

  trackByPerson = (_: number, person: ChatPerson) => person.user_id;
}
